import { forwardRef } from 'react';
import type {
  PropsWithoutRef,
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  FocusEvent
} from 'react';
import creditCardType from 'credit-card-type';

import { createClassName } from '../../utils';
import type { RefElement } from '../../types';
import Input from '../Input';
import { useConsolidatedRef, useInputFormatter } from '../../hooks';

import type { CreditCardInputProps } from './CreditCard.types';
import { formatCreditCard, isPossibleCardNumber } from './utils';

export default forwardRef<RefElement<CreditCardInputProps>, PropsWithoutRef<CreditCardInputProps>>(
  function CreditCardInput(
    { value = '', onChange, onBlur, onFocus, className, status, ...restProps },
    ref
  ) {
    const onInputKeyDown = (e: KeyboardEvent) => {
      if (value && !isPossibleCardNumber(value) && Number.isFinite(Number.parseInt(e.key, 10))) {
        (e.target as HTMLInputElement).value = '';
      }
    };

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value ? e.target.value.replace(/\D+/g, '') : undefined);
    };

    const onInputBlur = (e: FocusEvent) => {
      const inputValue = (e.target as HTMLInputElement).value;
      onBlur?.(inputValue ? inputValue.replace(/\D+/g, '') : undefined);
    };

    const onInputFocus = (e: FocusEvent) => {
      const inputValue = (e.target as HTMLInputElement).value;
      onFocus?.(inputValue ? inputValue.replace(/\D+/g, '') : undefined);
    };

    const onPaste = (e: ClipboardEvent) => {
      const text = e.clipboardData.getData('text').replace(/\D+/g, '');
      const [{ lengths: [inferredLength] = [] } = {}] = creditCardType(text);
      if (text.length > inferredLength) {
        onChange(Number(text).toString());
        e.preventDefault();
      }
    };

    const maxLength = value ? Math.max(...(creditCardType(value)[0]?.lengths || [16])) : undefined;

    const obfuscation = /\D/.test(value) ? 'partial' : 'none';

    const inputRef = useConsolidatedRef(ref);
    const formatted = useInputFormatter(inputRef, value, (input: string) =>
      formatCreditCard(input.replace(/\D+/g, ''), { obfuscation })
    );

    return (
      <Input
        {...restProps}
        status={status}
        className={createClassName('credit-card-input', className)}
        inputMode='numeric'
        ref={inputRef}
        value={formatted}
        onChange={onInputChange}
        onBlur={onInputBlur}
        onFocus={onInputFocus}
        onPaste={onPaste}
        onKeyDown={onInputKeyDown}
        maxLength={maxLength}
      />
    );
  }
);
